import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A lesson tab or sub-tab, held in the URL — `?tab=practice&view=quiz`.
 *
 * The lesson page kept both in component state, so a tab could only ever be
 * reached by clicking to it: a link could not open a lesson on its quiz, the
 * back button forgot where you were, and a static story could not show the
 * page on any tab but the first. Now the URL is the state.
 *
 * - `key` is `tab` (Lesson | Notes | Practice | Appendix), `view` (the
 *   sub-tab inside it) or `panel` (the left panel's Prompt | Sources). A value outside `allowed` reads as `fallback`, so a
 *   stale or hand-typed link lands somewhere real rather than on nothing.
 * - Writes REPLACE the history entry: moving between tabs is not navigation,
 *   and a back button that stepped through every tab clicked would be worse.
 * - Changing `tab` drops `view`. Each tab opens on its own first sub-tab, as
 *   it did before; whether a tab should remember its sub-tab is an open
 *   product question, and the URL does not answer it by accident.
 * - `urlNames` writes a tab under a different name than its id (the Overview
 *   tab is `mindmap` in code, `?view=overview` in the address bar). Its id
 *   is still read, so old links keep working.
 */
export function useLessonTabParam<T extends string>(
  key: 'tab' | 'view' | 'panel',
  allowed: readonly T[],
  fallback: T,
  urlNames: Partial<Record<T, string>> = {}
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const value = fromUrl(params.get(key), allowed, urlNames) ?? fallback;

  const setValue = useCallback(
    (next: T) => {
      setParams(
        prev => {
          // Re-picking the current tab is not a change: it must not drop the
          // sub-tab and bounce the user back to Text.
          const name = urlNames[next] ?? next;
          if (prev.get(key) === name) return prev;
          const p = new URLSearchParams(prev);
          p.set(key, name);
          if (key === 'tab') p.delete('view');
          // A text section belongs to the Text view it was opened in.
          if (key === 'tab' || key === 'view') p.delete('section');
          return p;
        },
        { replace: true }
      );
    },
    // urlNames is a module constant at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, setParams]
  );

  return [value, setValue];
}

function fromUrl<T extends string>(
  raw: string | null,
  allowed: readonly T[],
  urlNames: Partial<Record<T, string>>
): T | null {
  if (!raw) return null;
  const named = allowed.find(id => urlNames[id] === raw);
  if (named) return named;
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : null;
}
